import { useTranslation } from 'react-i18next';
import { isoToJalali, jalaliToIso, monthLength, monthName, toFaDigits } from '../lib/jalali';

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (iso: string) => void;
  /** Jalali years to offer, in display order. */
  years: number[];
  error?: string;
};

/** Day / month / year selects in the Persian calendar; emits an ISO (Gregorian) date or ''. */
export function JalaliDateSelect({ id, label, value, onChange, years, error }: Props) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const { jy, jm, jd } = readParts(value);

  const num = (n: number) => (isFa ? toFaDigits(n) : String(n));

  function emit(nextY: number, nextM: number, nextD: number) {
    if (!nextY || !nextM || !nextD) {
      onChange(pendingIso(nextY, nextM, nextD));
      return;
    }
    const day = Math.min(nextD, monthLength(nextY, nextM));
    onChange(jalaliToIso(nextY, nextM, day));
  }

  const days = Array.from({ length: jy && jm ? monthLength(jy, jm) : 31 }, (_, i) => i + 1);

  return (
    <fieldset className={`sa-field jalali-date${error ? ' has-error' : ''}`}>
      <legend className="sa-label" id={`${id}-label`}>
        {label}
      </legend>
      <div className="jalali-date__selects">
        <select
          id={`${id}-day`}
          className="sa-input"
          aria-label={t('common.day')}
          value={jd || ''}
          onChange={(e) => emit(jy, jm, Number(e.target.value))}
        >
          <option value="">{t('common.day')}</option>
          {days.map((d) => (
            <option key={d} value={d}>
              {num(d)}
            </option>
          ))}
        </select>
        <select
          id={`${id}-month`}
          className="sa-input"
          aria-label={t('common.month')}
          value={jm || ''}
          onChange={(e) => emit(jy, Number(e.target.value), jd)}
        >
          <option value="">{t('common.month')}</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {monthName(m, isFa)}
            </option>
          ))}
        </select>
        <select
          id={`${id}-year`}
          className="sa-input"
          aria-label={t('common.year')}
          value={jy || ''}
          onChange={(e) => emit(Number(e.target.value), jm, jd)}
        >
          <option value="">{t('common.year')}</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {num(y)}
            </option>
          ))}
        </select>
      </div>
      {error ? <p className="field-error">{error}</p> : null}
    </fieldset>
  );
}

const PENDING_PREFIX = 'jalali:';

/**
 * Partially chosen dates are kept as "jalali:Y-M-D" so the selects remember them;
 * callers treat any value that is not YYYY-MM-DD as incomplete.
 */
function pendingIso(jy: number, jm: number, jd: number): string {
  return jy || jm || jd ? `${PENDING_PREFIX}${jy}-${jm}-${jd}` : '';
}

function readParts(value: string): { jy: number; jm: number; jd: number } {
  if (value.startsWith(PENDING_PREFIX)) {
    const [jy = 0, jm = 0, jd = 0] = value.slice(PENDING_PREFIX.length).split('-').map(Number);
    return { jy, jm, jd };
  }
  return isoToJalali(value) ?? { jy: 0, jm: 0, jd: 0 };
}
